import {
  Box,
  StatusBanner,
  ParagraphText,
  GDSStatus,
} from "@leagueplatform/genesis-core";
import { InternalOrExternalLink } from "@leagueplatform/web-common-components";
import { ClaimsBannersAttributes } from "@leagueplatform/claims-api";
import { useServiceDetailsBanner } from "hooks/use-service-details-banner/use-service-details-banner.hook";

const BANNER_TYPE_TO_STATUS: Record<
  ClaimsBannersAttributes["bannerType"],
  GDSStatus
> = {
  info: "info",
  warning: "warning",
  success: "success",
  error: "error",
  danger: "error",
  brand: "brand",
  neutral: "neutral",
};

const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:"]);

const hasOwnScheme = (url: string) => {
  try {
    // eslint-disable-next-line no-new -- only used to test whether `url` parses without a base
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const normalizeLinkUrl = (url: string) =>
  hasOwnScheme(url) || url.startsWith("/") ? url : `/${url}`;

const isSafeLinkUrl = (url: string) => {
  try {
    return SAFE_LINK_PROTOCOLS.has(
      new URL(url, window.location.origin).protocol
    );
  } catch {
    return false;
  }
};

// The BE may return a `link` whose `text` names a vendor
// that also appears inside `bannerDescription`.
// Only that first occurrence becomes a clickable link; if the substring
// isn't found, the copy renders as plain text.
const renderDescription = (
  description: string,
  link: ClaimsBannersAttributes["link"]
) => {
  const linkIndex = link?.text ? description.indexOf(link.text) : -1;
  if (!link?.text || linkIndex === -1 || !isSafeLinkUrl(link.url))
    return description;

  const before = description.slice(0, linkIndex);
  const after = description.slice(linkIndex + link.text.length);

  return (
    <>
      {before}
      <InternalOrExternalLink href={normalizeLinkUrl(link.url)}>
        {link.text}
      </InternalOrExternalLink>
      {after}
    </>
  );
};

export const ServiceDetailsBanner = () => {
  const { data: banner } = useServiceDetailsBanner();

  if (!banner?.bannerTitle) return null;

  const { bannerType, bannerTitle, bannerDescription, link } = banner;

  return (
    <Box css={{ marginY: "$half" }}>
      <StatusBanner
        status={BANNER_TYPE_TO_STATUS[bannerType]}
        showIcon
        icon="statusInformationCircleFill"
        title={bannerTitle}
        headingLevel="3"
      >
        {bannerDescription && (
          <ParagraphText size="sm">
            {renderDescription(bannerDescription, link)}
          </ParagraphText>
        )}
      </StatusBanner>
    </Box>
  );
};
